import { WORKFLOW_CHANNEL_TYPE } from '@/types/workflowConst';

import { CONDITION_OPERATOR, type SignalAlertFormData } from '../../model';

/**
 * Backend LogicalOP enum 값
 * - 1: AND
 * - 2: OR
 * (0, 3, 4는 현재 사용되지 않음)
 */
type LogicalOp = 1 | 2;

const LOGICAL_OP = {
  AND: 1,
  OR: 2,
} as const satisfies Record<string, LogicalOp>;

/**
 * Form 데이터를 백엔드 SignalAlertWorkflowState로 변환
 */
export function mapFormDataToSignal(
  formData: SignalAlertFormData,
): SignalAlertWorkflowState {
  const opMap: Record<
    (typeof CONDITION_OPERATOR)[keyof typeof CONDITION_OPERATOR],
    LogicalOp
  > = {
    [CONDITION_OPERATOR.AND]: LOGICAL_OP.AND,
    [CONDITION_OPERATOR.OR]: LOGICAL_OP.OR,
  };

  const needsOperator =
    formData.changeRateCondition.enabled &&
    formData.feedbackCountCondition.enabled;

  return {
    signal_ids: formData.signals,
    filter_set: formData.filter,
    duration_days: formData.duration,
    trigger: {
      feedback_count: formData.feedbackCountCondition.enabled
        ? formData.feedbackCountCondition.threshold
        : undefined,
      change_rate: formData.changeRateCondition.enabled
        ? formData.changeRateCondition.threshold
        : undefined,
      op: needsOperator ? opMap[formData.conditionOperator] : 0,
    },
  };
}

/**
 * 백엔드 SignalAlertWorkflowState를 Form 데이터로 변환
 * title은 workflow 레벨에서 별도로 전달되므로 여기서는 반환하지 않음
 */
export function mapSignalToFormData(
  signal: SignalAlertWorkflowState,
  channel: WorkflowChannel,
): Omit<SignalAlertFormData, 'title'> {
  const opReverseMap: Record<
    LogicalOp,
    (typeof CONDITION_OPERATOR)[keyof typeof CONDITION_OPERATOR]
  > = {
    [LOGICAL_OP.AND]: CONDITION_OPERATOR.AND,
    [LOGICAL_OP.OR]: CONDITION_OPERATOR.OR,
  };

  // op 값이 예상 범위(1, 2) 밖이면 AND로 fallback
  const op = signal.trigger.op as LogicalOp;
  const conditionOperator = opReverseMap[op] ?? CONDITION_OPERATOR.AND;

  return {
    signals: signal.signal_ids ?? [],
    duration: signal.duration_days,
    feedbackCountCondition: {
      enabled: signal.trigger.feedback_count != null,
      threshold: signal.trigger.feedback_count ?? 100,
    },
    conditionOperator,
    changeRateCondition: {
      enabled: signal.trigger.change_rate != null,
      threshold: signal.trigger.change_rate ?? 10,
    },
    filter: signal.filter_set,
    channel: mapChannelToFormData(channel),
  };
}

/**
 * 백엔드 WorkflowChannel을 Form의 discriminated union 타입으로 변환
 */
function mapChannelToFormData(
  channel: WorkflowChannel,
): SignalAlertFormData['channel'] {
  if (channel.type === WORKFLOW_CHANNEL_TYPE.SLACK) {
    return {
      type: WORKFLOW_CHANNEL_TYPE.SLACK,
      slack_webhook_id: channel.slack_webhook_id ?? '',
    };
  }
  return {
    type: WORKFLOW_CHANNEL_TYPE.MAIL,
    mail_address: channel.mail_address ?? '',
  };
}

/**
 * 중복 제목 검증 함수
 * @param title - 검증할 제목
 * @param existingTitles - 기존 제목 Set
 * @returns 에러 메시지 또는 undefined
 */
export function validateDuplicateTitle(
  title: string,
  existingTitles: Set<string>,
): string | undefined {
  if (existingTitles.has(title)) {
    return 'This title is already in use';
  }
  return undefined;
}
